import { pushSnackbar } from 'FRONTEND/actions/app';
import { isScheduleAvailable } from 'FRONTEND/components/Calendar/utils';
import {
  createError, createRequest, createSuccess,
  createRecurrenceError, createRecurrenceRequest, createRecurrenceSuccess,
  deleteError, deleteRequest, deleteSuccess,
  listError, listRequest, listSuccess,
  updateError, updateRequest, updateSuccess,
  clear,
  stopEditing,
  selectActorAvailabilities,
} from 'FRONTEND/reducers/availabilitySlice';
import { goToMultipleCalendar, pickEvent } from 'FRONTEND/reducers/calendarSlice';
import * as availabilityApi from 'FRONTEND/utils/backendApi/availabilityApi';
import { DRAFT_AVAILABILITY_ID } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';

const availabilityMiddleware = (store) => (next) => (action) => {
  switch (action.type) {
    case listRequest.toString():
      availabilityApi.getAvailabilities({ actorId: action.payload.actorId, actorType: action.payload.actorType, startDate: action.payload.startDate, endDate: action.payload.endDate })
        .then((availabilities) => store.dispatch(listSuccess({ availabilities })))
        .catch((error) => store.dispatch(listError({ error: error.message })));
      return next(action);

    case createRequest.toString():
      if (!isScheduleAvailable({
        availabilities: selectActorAvailabilities(store.getState(), action.payload.actorId).filter(({ availabilityId }) => availabilityId !== DRAFT_AVAILABILITY_ID),
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      })) {
        next(action);
        store.dispatch(createSuccess({}));
        return;
      }
      next(action);

      availabilityApi.createAvailability({
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
        typeList: action.payload.typeList,
        actorId: action.payload.actorId,
        actorType: action.payload.actorType,
        locationId: action.payload.locationId,
        tenantList: action.payload.tenantList,
      })
        .then((availability) => {
          store.dispatch(createSuccess({ availability }));
        })
        .catch((error) => store.dispatch(createError({ error: error.message })));
      return;

    case createSuccess.toString():
      next(action);
      if (action.payload.availability) {
        store.dispatch(pickEvent({ eventId: action.payload.availability.availabilityId }));
      }
      return;

    case updateRequest.toString():
      availabilityApi.updateAvailability({
        availabilityId: action.payload.availabilityId,
        actorId: action.payload.actorId,
        actorType: action.payload.actorType,
        locationId: action.payload.locationId,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
        typeList: action.payload.typeList,
        tenantList: action.payload.tenantList,
      }).then((availability) => {
        store.dispatch(stopEditing({ id: action.payload.availabilityId, shouldRestore: false }));
        store.dispatch(updateSuccess({ availability }));
      }).catch((error) => {
        if ([availabilityApi.OVERLAP_ERROR, availabilityApi.BOOKED_ERROR].includes(error.message)) {
          store.dispatch(stopEditing({ id: action.payload.availabilityId, shouldRestore: true }));
          let message = '';
          if (error.message === 'overlap') {
            message = i18n._('Availability cannot be changed or moved there because it would overlap with another one.');
          } else if (error.message === 'booked') {
            message = i18n._('You can’t delete or modify an availability when an active appointment is booked on it. Please edit your appointments before modifying the availability.');
          }
          store.dispatch(pushSnackbar({
            message,
            variant: 'warning',
          }));
        }
        store.dispatch(updateError({ error: error.message }));
      });
      return next(action);

    case updateSuccess.toString(): {
      store.dispatch(stopEditing({ id: action.payload.availability.availabilityId }));
      return next(action);
    }

    case deleteRequest.toString():
      availabilityApi.deleteAvailabilities({ availabilityId: action.payload.availabilityId })
        .then(() => {
          store.dispatch(deleteSuccess({ availabilityId: action.payload.availabilityId }));
        }).catch((error) => store.dispatch(deleteError({ error: error.message })));
      return next(action);

    case createRecurrenceRequest.toString():
      availabilityApi.createAvailabilitiesRecurrence({
        availabilityId: action.payload.availabilityId,
        endDate: action.payload.endDate,
      }).then(({ overlapping, availabilities }) => {
        if (overlapping) {
          store.dispatch(createRecurrenceSuccess());
          const overlappingDates = overlapping.join(', ');
          const message = i18n._('Availabilities could not be created on the following dates because they overlap with existing ones');
          store.dispatch(pushSnackbar({
            message: `${message}: ${overlappingDates}.`,
            variant: 'warning',
          }));
        } else {
          store.dispatch(createRecurrenceSuccess({ availabilities }));
          store.dispatch(pushSnackbar({
            message: i18n._('Availabilities created.'),
            variant: 'success',
          }));
        }
      }).catch((error) => store.dispatch(createRecurrenceError({ error: error.message })));
      return next(action);

    case goToMultipleCalendar.toString():
      store.dispatch(clear({ expertId: store.getState().calendar.selectedExpertId }));
      return next(action);

    default:
      return next(action);
  }
};

export default availabilityMiddleware;
